import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { PositioningCreationModal, FormData as ModalFormData } from '@/components/positioning/PositioningCreationModal';
import { ConfirmModal } from '@/components/shared/ConfirmModal';
import { SubscriptionLimitBanner } from '@/components/shared/SubscriptionLimitBanner';
import { AiItemsList } from '@/components/shared/AiItemsList';
import { AiDetailPanel } from '@/components/shared/AiDetailPanel';
import { useAiPagePermissions } from '@/hooks/useAiPagePermissions';
import { projectsApi, positioningsApi, marketAnalysesApi, personasApi } from '@/utils/api-pb';
import { pb } from '@/utils/pocketbase';
import type { Project, Positioning, MarketAnalysis, Persona } from '@/utils/pocketbase';
import { API_URL } from '@/utils/config';


import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/i18n/LanguageContext';
const FREE_POSITIONING_LIMIT = 0;

export default function PositioningPage() {
  const { t } = useTranslation('positioning');
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Data state
  const [projects, setProjects] = useState<Project[]>([]);
  const [positionings, setPositionings] = useState<Positioning[]>([]);
  const [marketAnalyses, setMarketAnalyses] = useState<MarketAnalysis[]>([]);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [selectedPositioning, setSelectedPositioning] = useState<Positioning | null>(null);

  // Form state
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [positioningToDelete, setPositioningToDelete] = useState<string | null>(null);

  const [formData, setFormData] = useState<ModalFormData>({
    project_id: '',
    persona_id: '',
    market_analysis_id: '',
    competitors: '',
    unique_advantage: '',
    price_segment: '',
    sales_channels: '',
    mission: '',
    comment: ''
  });

  // Permissions
  const { isFree, canCreateNew, canEdit, canDelete } = useAiPagePermissions(positionings.length, FREE_POSITIONING_LIMIT);

  // Tooltip state for create button
  const [tooltipVisible, setTooltipVisible] = useState<{ id: string, type: string } | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  // Auto-select positioning (prefer recordId from query param, fallback to first)
  useEffect(() => {
    if (positionings.length > 0 && !selectedPositioning) {
      const targetId = searchParams.get('recordId');
      const target = targetId ? positionings.find(p => p.id === targetId) : null;
      setSelectedPositioning(target ?? positionings[0] ?? null);
    }
  }, [positionings, selectedPositioning, searchParams]);

  const loadData = async () => {
    try {
      setIsInitialLoading(true);
      const [projectsResult, positioningsResult, analysesResult, personasResult] = await Promise.all([
        projectsApi.list(),
        positioningsApi.list(),
        marketAnalysesApi.list(),
        personasApi.list(),
      ]);
      setProjects(projectsResult);
      setPositionings(positioningsResult);
      setMarketAnalyses(analysesResult);
      setPersonas(personasResult);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsInitialLoading(false);
    }
  };

  const handleSubmitModal = async (modalFormData: ModalFormData) => {
    setIsLoading(true);

    try {
      const selectedProject = projects.find(p => p.id === modalFormData.project_id);

      const descriptionParts: string[] = [];
      if (selectedProject?.name) descriptionParts.push(`Продукт: ${selectedProject.name}`);
      if (selectedProject?.description) descriptionParts.push(`Описание: ${selectedProject.description}`);
      if (selectedProject?.market) descriptionParts.push(`Рынок: ${selectedProject.market}`);
      if (modalFormData.competitors) descriptionParts.push(`Конкуренты: ${modalFormData.competitors}`);
      if (modalFormData.unique_advantage) descriptionParts.push(`Уникальное преимущество: ${modalFormData.unique_advantage}`);
      if (modalFormData.price_segment) descriptionParts.push(`Ценовой сегмент: ${modalFormData.price_segment}`);
      if (modalFormData.sales_channels) descriptionParts.push(`Каналы продаж: ${modalFormData.sales_channels}`);
      if (modalFormData.mission) descriptionParts.push(`Миссия: ${modalFormData.mission}`);

      const productDescription = descriptionParts.join('\n');

      const response = await fetch(`${API_URL}/api/ai/positioning`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${pb.authStore.token}`,
        },
        body: JSON.stringify({
          projectId: modalFormData.project_id,
          productDescription,
          personaId: modalFormData.persona_id || undefined,
          marketAnalysisId: modalFormData.market_analysis_id || undefined,
          comment: modalFormData.comment || undefined,
          lang,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || t('toast.createError'));
      }

      setShowEditModal(false);
      setShowNewModal(false);
      setFormData({
        project_id: '',
        persona_id: '',
        market_analysis_id: '',
        competitors: '',
        unique_advantage: '',
        price_segment: '',
        sales_channels: '',
        mission: '',
        comment: ''
      });
      loadData();
    } catch (error) {
      console.error('Error creating/updating positioning:', error);
      const errorMessage = error instanceof Error ? error.message : t('toast.createError');
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await positioningsApi.delete(id);
      setShowDeleteConfirm(false);
      setPositioningToDelete(null);
      if (selectedPositioning?.id === id) {
        setSelectedPositioning(null);
      }
      loadData();
    } catch (error) {
      console.error('Error deleting positioning:', error);
      toast.error(t('toast.deleteError'));
    }
  };

  const handleCreateNew = () => {
    if (!canCreateNew) return;

    setFormData({
      project_id: '',
      persona_id: '',
      market_analysis_id: '',
      competitors: '',
      unique_advantage: '',
      price_segment: '',
      sales_channels: '',
      mission: '',
      comment: ''
    });
    setIsEditing(false);
    setSelectedPositioning(null);
    setShowNewModal(true);
  };

  const handleEditPositioning = (positioning: Positioning) => {
    if (!canEdit) return;

    setFormData({
      project_id: positioning.project || '',
      persona_id: positioning.persona || '',
      market_analysis_id: positioning.market_analysis || '',
      competitors: '',
      unique_advantage: '',
      price_segment: '',
      sales_channels: '',
      mission: '',
      comment: ''
    });
    setSelectedPositioning(positioning);
    setIsEditing(true);
    setShowEditModal(true);
  };

  const handleSelectPositioning = (positioning: Positioning) => {
    setSelectedPositioning(positioning);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setShowNewModal(false);
    setIsEditing(false);
  };

  // Get positioning title based on product name
  const getPositioningTitle = (positioning: Positioning): string => {
    const project = projects.find(p => p.id === positioning.project);
    return t('itemTitle', { projectName: project?.name || t('defaultProjectName') });
  };

  // Count positionings per project for numbering (chronological: oldest = 1)
  const positioningNumbers = useMemo(() => {
    const projectCounts: Record<string, number> = {};
    const numbers: Record<string, { displayNumber: number; total: number }> = {};

    positionings.forEach(positioning => {
      const projectId = positioning.project || '';
      projectCounts[projectId] = (projectCounts[projectId] || 0) + 1;
    });

    // Data is already sorted by created (oldest first)
    const projectIndexes: Record<string, number> = {};
    positionings.forEach(positioning => {
      const projectId = positioning.project || '';
      projectIndexes[projectId] = (projectIndexes[projectId] || 0) + 1;
      numbers[positioning.id] = {
        displayNumber: projectIndexes[projectId] ?? 1,
        total: projectCounts[projectId] ?? 1
      };
    });

    return numbers;
  }, [positionings]);

  const getPositioningListTitle = (positioning: Positioning): string => {
    const numberInfo = positioningNumbers[positioning.id];
    const shouldShowNumber = numberInfo && numberInfo.total > 1;
    const displayNum = numberInfo?.displayNumber ?? 1;
    return `${getPositioningTitle(positioning)}${shouldShowNumber ? ` ${displayNum}` : ''}`;
  };

  if (!isInitialLoading && isFree) {
    return (
      <><PageHeader title={t('title')} description={t('description')} />
        <SubscriptionLimitBanner
          title={t('paidBanner.title')}
          description={t('paidBanner.description')}
        />
      </>
    );
  }

  return (
    <>{isInitialLoading ? (
          /* Initial Loading State */
          <div className="flex flex-col items-center justify-center gap-[16px] py-[80px]">
            <h2 className="font-manrope font-bold text-[32px] leading-[40px] text-[#0d0d0d] text-center">
              {t('title')}
            </h2>
            <p className="font-segoe text-[16px] leading-[24px] text-[#565858] text-center">
              {t('loading')}
            </p>
            <div className="w-[32px] h-[32px] border-2 border-[#2197f1] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : positionings.length === 0 ? (
          /* Empty State */
          <><PageHeader title={t('title')} description={t('description')} />
          <div className="flex flex-col gap-[24px]">
            <div className="font-segoe text-[16px] leading-[24px] text-[#565858] max-w-[769px]">
              <p>
                {t('empty.text')}
              </p>
            </div>

            <button
              onClick={handleCreateNew}
              disabled={!canCreateNew}
              onMouseEnter={() => !canCreateNew && setTooltipVisible({ id: 'create-new', type: 'create' })}
              onMouseLeave={() => setTooltipVisible(null)}
              className="relative inline-block"
            >
              <div className={`${canCreateNew
                ? 'bg-[#2197f1] hover:bg-[#1a7bc7]'
                : 'bg-[rgba(33,151,241,0.2)] cursor-not-allowed opacity-60'
                } rounded-[16px] px-[24px] py-[12px] transition-colors inline-block`}>
                <p className="font-segoe font-semibold text-[16px] leading-[24px] text-white text-center">
                  {t('newButton')}
                </p>
              </div>

              {tooltipVisible?.id === 'create-new' && !canCreateNew && (
                <div className="absolute top-[-60px] left-1/2 -translate-x-1/2 bg-[rgba(13,13,13,0.8)] px-[24px] py-[12px] rounded-[16px] whitespace-nowrap z-10">
                  <p className="font-segoe text-[14px] leading-[20px] text-white text-center">
                    {t('tooltip.freeLimitCreate')}
                  </p>
                </div>
              )}
            </button>
          </div>
          </>
        ) : (
          /* List with Selected Positioning */
          <div className="flex flex-col lg:flex-row gap-[24px]">
            {/* Left Sidebar - Positionings List */}
            <div className="flex flex-col gap-[8px] w-full lg:w-[424px] lg:flex-shrink-0 lg:sticky lg:top-[24px] lg:max-h-[calc(100vh-100px)] lg:overflow-y-auto">
              <PageHeader title={t('title')} description={t('description')} />
              <AiItemsList
                items={positionings}
                selectedId={selectedPositioning?.id ?? null}
                getItemTitle={getPositioningListTitle}
                onSelect={handleSelectPositioning}
                isItemDisabled={(_item, index) => isFree && index > 0}
                tooltipMessage={t('tooltip.paidOnly')}
              />

              {/* Create New Button */}
              <button
                onClick={handleCreateNew}
                disabled={!canCreateNew}
                onMouseEnter={() => !canCreateNew && setTooltipVisible({ id: 'create-new-sidebar', type: 'create' })}
                onMouseLeave={() => setTooltipVisible(null)}
                className="relative"
              >
                <div className={`${canCreateNew
                  ? 'bg-[#2197f1] hover:bg-[#1a7bc7]'
                  : 'bg-[rgba(33,151,241,0.2)] cursor-not-allowed opacity-60'
                  } rounded-[16px] px-[24px] py-[12px] transition-colors`}>
                  <p className="font-segoe font-semibold text-[16px] leading-[24px] text-white text-center">
                    {t('newButton')}
                  </p>
                </div>

                {tooltipVisible?.id === 'create-new-sidebar' && !canCreateNew && (
                  <div className="absolute top-[-60px] left-1/2 -translate-x-1/2 bg-[rgba(13,13,13,0.8)] px-[24px] py-[12px] rounded-[16px] whitespace-nowrap z-10">
                    <p className="font-segoe text-[14px] leading-[20px] text-white text-center">
                      {t('tooltip.freeLimitCreate')}
                    </p>
                  </div>
                )}
              </button>

            </div>

            {/* Right Content - Selected Positioning */}
            {selectedPositioning && (
              <AiDetailPanel
                key={selectedPositioning.id}
                title={getPositioningTitle(selectedPositioning)}
                content={selectedPositioning.generated_text}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={() => handleEditPositioning(selectedPositioning)}
                onDelete={() => {
                  setPositioningToDelete(selectedPositioning.id);
                  setShowDeleteConfirm(true);
                }}
                editButtonText={t('detail.editButton')}
                actionButtons={[
                  { text: t('detail.actionMvpPlan'), onClick: () => navigate('/mvp-plan') }
                ]}
                pdfExportData={selectedPositioning.generated_text ? {
                  title: getPositioningTitle(selectedPositioning),
                  content: selectedPositioning.generated_text,
                  artifactType: 'positioning',
                  artifactTypeLabel: t('detail.pdfLabel'),
                  projectName: projects.find(p => p.id === selectedPositioning.project)?.name || t('defaultProjectName'),
                  date: selectedPositioning.created,
                } : undefined}
                tooltipEditText={t('tooltip.paidOnly')}
                tooltipDeleteText={t('tooltip.paidOnly')}
                emptyText={t('detail.emptyText')}
              />
            )}
          </div>
        )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setPositioningToDelete(null);
        }}
        onConfirm={() => positioningToDelete && handleDelete(positioningToDelete)}
        title={t('confirm.deleteTitle')}
        description={t('confirm.deleteDescription')}
        confirmText={t('confirm.deleteConfirm')}
        cancelText={t('confirm.deleteCancel')}
      />

      {/* Positioning Modal (Create/Edit) */}
      <PositioningCreationModal
        isOpen={showNewModal || showEditModal}
        onClose={handleCloseModal}
        onSubmit={handleSubmitModal}
        projects={projects}
        marketAnalyses={marketAnalyses}
        personas={personas}
        initialData={formData}
        isEditing={isEditing}
        isLoading={isLoading}
      />
    </>
  );
}
